import React, {useEffect, useState} from 'react';
import {Row, Col, Card} from 'react-bootstrap';
import CellLoader from '@/components/loading/CellLoader';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import Icon from '@/components/icons/Icon';
import {Badge} from '@/components/ui';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {buildURL} from '@/utils/urlBuilder';

interface LicenseData {
    license_id?: string;
    license_type?: string;
    subscriber?: string;
    issuer?: string;
    issued?: string;
    expires?: string;
    product?: string;
    modules?: Record<string, boolean>;
}

const LicenseView: React.FC = React.memo(() => {
    const {userData, config, loading: loadingUserData} = useOptimizedConfig();
    const [licenseData, setLicenseData] = useState<LicenseData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const isLicenseExpired = (expiryDate: string): boolean => {
        if (!expiryDate) {return false;}
        const expiry = new Date(expiryDate);
        const now = new Date();
        return expiry < now;
    };

    const getLicenseStatus = (expiryDate: string): {status: string; variant: string; icon: 'mdi-help-circle' | 'mdi-close' | 'mdi-alert' | 'mdi-alert-circle' | 'mdi-check'} => {
        if (!expiryDate) {return {status: i18n.t('jt_license_status_unknown.text'), variant: 'secondary', icon: 'mdi-help-circle'};}

        const expiry = new Date(expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) {
            return {status: i18n.t('jt_license_status_expired.text'), variant: 'danger', icon: 'mdi-close'};
        } else if (daysUntilExpiry <= 7) {
            return {status: i18n.t('jt_license_status_expiring_soon.text'), variant: 'warning', icon: 'mdi-alert'};
        } else if (daysUntilExpiry <= 30) {
            return {status: i18n.t('jt_license_status_active.text'), variant: 'warning', icon: 'mdi-alert-circle'};
        } else {
            return {status: i18n.t('jt_license_status_active.text'), variant: 'success', icon: 'mdi-check'};
        }
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) {return 'N/A';}
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    useEffect(() => {
        const fetchLicenseData = async () => {
            try {
                const response = await fetch(buildURL('adminLicense'));
                if (!response.ok) {
                    throw new Error('Failed to fetch license data');
                }
                const data = await response.json();
                setLicenseData(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Unknown error');
            } finally {
                setLoading(false);
            }
        };

        fetchLicenseData();
    }, []);

    const userCanAccessLicense = hasPermission('fs.application.license.read', userData?.permissions, true);

    if (!userCanAccessLicense && !loadingUserData) {
        return (
            <>
                <ConditionalNav>

                </ConditionalNav>

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    if (loadingUserData || loading) {
        return <CellLoader fullHeight={true} />;
    }

    if (error) {
        return (
            <>
                <ConditionalNav>

                </ConditionalNav>

                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </>
        );
    }

    const isSaasLicense = !licenseData?.license_id;

    if (!licenseData) {
        return <CellLoader fullHeight={true} />;
    }

    const licenseStatus = getLicenseStatus(licenseData.expires || '');

    return (
        <>
            <ConditionalNav />

            {/* License Status Header */}
            <Row className="mb-4 pr-3">
                <Col lg={12}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center justify-content-between pr-4">
                            <div className="d-flex align-items-center">
                                <Icon name={isSaasLicense ? "mdi-gauge" : "mdi-checkbox-marked-outline"} size="2x" className="text-info mr-3" />
                                <div>
                                    <h4 className="mb-1">
                                        {isSaasLicense ? i18n.t('jt_saas_license.text') : i18n.t('jt_license_information.text')}
                                    </h4>
                                    <p className="text-muted mb-0">
                                        {isSaasLicense
                                            ? i18n.t('jt_this_installation_uses_a_saas_license_model.text')
                                            : `${i18n.t('jt_product_label.text')} ${licenseData.product}`
                                        }
                                    </p>
                                </div>
                            </div>
                            {!isSaasLicense && (
                                <Badge
                                    variant={licenseStatus.variant as 'danger' | 'warning' | 'success' | 'secondary'}
                                    size="lg"
                                    icon={licenseStatus.icon}
                                >
                                    {licenseStatus.status}
                                </Badge>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* License Details Grid - Only for non-SaaS licenses */}
            {!isSaasLicense && (
                <Row className="pr-3">
                    <Col lg={4} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-light border-0">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <Icon name="mdi-information-outline" className="mr-2 text-info" />
                                    {i18n.t('jt_license_details.text')}
                                </h6>
                            </Card.Header>
                            <Card.Body className="pr-4">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <small className="text-muted text-uppercase fw-bold">{i18n.t('jt_license_id.text')}</small>
                                        <div className="font-monospace small bg-light text-dark p-2 rounded mt-1">
                                            {licenseData.license_id}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted text-uppercase fw-bold">{i18n.t('jt_license_type.text')}</small>
                                        <div className="mt-1">
                                            <Badge variant="light" size="sm">
                                                {licenseData.license_type}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted text-uppercase fw-bold">{i18n.t('jt_product.text')}</small>
                                        <div className="mt-1 fw-semibold text-capitalize">
                                            {licenseData.product}
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-light border-0">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <Icon name="mdi-account-group" className="mr-2 text-info" />
                                    {i18n.t('jt_organization_details.text')}
                                </h6>
                            </Card.Header>
                            <Card.Body className="pr-4">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <small className="text-muted text-uppercase fw-bold">{i18n.t('jt_issued_for.text')}</small>
                                        <div className="mt-1 d-flex align-items-center">
                                            <Icon name="mdi-account-box" className="mr-2 text-muted" />
                                            <span className="fw-semibold">{licenseData.subscriber}</span>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <small className="text-muted text-uppercase fw-bold">{i18n.t('jt_issued_by.text')}</small>
                                        <div className="mt-1 d-flex align-items-center">
                                            <Icon name="mdi-lock" className="mr-2 text-muted" />
                                            <span className="fw-semibold">{licenseData.issuer}</span>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-light border-0">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <Icon name="mdi-gauge" className="text-info" />
                                    <span>{i18n.t('jt_license_timeline.text')}</span>
                                </h6>
                            </Card.Header>
                            <Card.Body className="pr-4">
                                <div className="row text-center">
                                    <div className="col-md-6">
                                        <div className="border-end border-light pr-3">
                                            <Icon name="mdi-plus" size="2x" className="text-success mb-2" />
                                            <h6 className="text-muted text-uppercase fw-bold mb-1">{i18n.t('jt_issued_date.text')}</h6>
                                            <div className="fw-semibold">{formatDate(licenseData.issued || '')}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="pl-3">
                                            <Icon
                                                name={isLicenseExpired(licenseData.expires || '') ? 'mdi-close' : 'mdi-check'}
                                                size="2x"
                                                className={`mb-2 ${isLicenseExpired(licenseData.expires || '') ? 'text-danger' : 'text-warning'}`}
                                            />
                                            <h6 className="text-muted text-uppercase fw-bold mb-1">{i18n.t('jt_expiry_date.text')}</h6>
                                            <div className={`fw-semibold ${isLicenseExpired(licenseData.expires || '') ? 'text-danger' : ''}`}>
                                                {formatDate(licenseData.expires || '')}
                                            </div>
                                            {licenseData.expires && (
                                                <small className="text-muted">
                                                    {isLicenseExpired(licenseData.expires)
                                                        ? i18n.t('jt_license_has_expired.text')
                                                        : i18n.t('jt_license_days_remaining.text', {days: Math.ceil((new Date(licenseData.expires).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))})
                                                    }
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                </Row>
            )}

            {/* License Timeline */}
            <Row className="mb-4">

            </Row>

            {/* Modules Information */}
            {(() => {

                const modules = licenseData.modules || {};
                const hasModules = Object.keys(modules).length > 0;
                const coreModules = Object.entries(modules).filter(([moduleName]) => moduleName.startsWith('core-'));
                const addonModules = Object.entries(modules).filter(([moduleName]) => moduleName.startsWith('addon-'));

                const renderModuleCard = (moduleName: string, isActive: boolean) => {
                    const moduleTitleKey = `jt_module_${moduleName.replace(/-/g, '_')}_title`;
                    const moduleDescriptionKey = `jt_module_${moduleName.replace(/-/g, '_')}_description`;
                    const moduleTitle = i18n.t(`${moduleTitleKey}.text`, {defaultValue: moduleName.replace(/-/g, ' ')});
                    const moduleDescription = i18n.t(`${moduleDescriptionKey}.text`, {defaultValue: ''});

                    return (
                        <div key={moduleName} className="col-md-6 col-lg-4 mb-3">
                            <div className="p-3 border rounded h-100">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                        <Icon
                                            name={isActive ? 'mdi-check-circle' : 'mdi-close-circle'}
                                            className={`mr-2 ${isActive ? 'text-success' : 'text-secondary'}`}
                                        />
                                        <span className="fw-semibold">
                                            {moduleTitle}
                                        </span>
                                    </div>
                                    <Badge variant={isActive ? 'success' : 'warning'} size="sm">
                                        {isActive ? i18n.t('jt_active.text') : i18n.t('jt_inactive.text')}
                                    </Badge>
                                </div>
                                {moduleDescription && (
                                    <div className="text-muted small mt-2">
                                        {moduleDescription}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                };

                return (
                    <>
                        {/* Core Modules */}
                        <Row className="pr-3 mb-4">
                            <Col lg={12}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-light border-0">
                                        <h6 className="mb-0 d-flex align-items-center">
                                            <Icon name="mdi-puzzle" className="text-info mr-2" />
                                            {i18n.t('jt_core_modules.text', {defaultValue: 'Core Modules'})}
                                        </h6>
                                    </Card.Header>
                                    <Card.Body className="pr-4">
                                        {coreModules.length > 0 ? (
                                            <div className="row g-2">
                                                {coreModules.map(([moduleName, isActive]) => renderModuleCard(moduleName, isActive))}
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted py-3">
                                                <Icon name="mdi-puzzle-outline" size="2x" className="mb-2" />
                                                <p className="mb-0">{i18n.t('jt_no_core_modules_available.text', {defaultValue: 'No core modules available'})}</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Additional Modules */}
                        {addonModules.length > 0 && (
                            <Row className="pr-3">
                                <Col lg={12}>
                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-light border-0">
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <Icon name="mdi-puzzle-outline" className="text-info mr-2" />
                                                {i18n.t('jt_additional_modules.text', {defaultValue: 'Additional Modules'})}
                                            </h6>
                                        </Card.Header>
                                        <Card.Body className="pr-4">
                                            <div className="row g-2">
                                                {addonModules.map(([moduleName, isActive]) => renderModuleCard(moduleName, isActive))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </>
                );
            })()}
        </>
    );
});

export default LicenseView;
